import { useState, useEffect, useRef, useCallback } from "react";
import { adminAPI } from "../../api";
import ModalPortal from "../../components/admin/ModalPortal";
import useEsMovil from "../../hooks/useEsMovil";
import { useAuth } from "../../context/AuthContext";
import { formatFecha, CONTACT_STATUS } from "../../utils";
import { etiquetaFormaPago } from "../../constants/formasPago";
import { telefonoValido, emailValido, normalizarTelefono } from "../../constants/telefono";
import toast from "react-hot-toast";
import "./Leads.css";
import "./LeadsMovil.css";

const FORM_VACIO = { name: "", email: "", phone: "", message: "", property_id: "", status: "NUEVO", internal_notes: "" };

export default function Leads() {
  const { esSuperAdmin } = useAuth();
  const [leads, setLeads] = useState([]);
  const [eliminando, setEliminando] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [cargando, setCargando] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState("");
  const [seleccionado, setSeleccionado] = useState(null);
  const [notas, setNotas] = useState("");

  // Modal nuevo lead
  const [modalAbierto, setModalAbierto] = useState(false);
  const [formNuevo, setFormNuevo] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);

  const cargar = async (status = filtroStatus, page = 1) => {
    setCargando(true);
    try {
      const params = { page, limit: 20 };
      if (status) params.status = status;
      const res = await adminAPI.listarLeads(params);
      setLeads(res.data.data);
      setPagination(res.data.pagination);
    } catch { toast.error("Error cargando leads"); }
    finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, []);

  const cambiarStatus = async (id, status) => {
    try {
      await adminAPI.actualizarLead(id, { status });
      setLeads((ls) => ls.map((l) => l.id === id ? { ...l, status } : l));
      if (seleccionado?.id === id) setSeleccionado((s) => ({ ...s, status }));
      toast.success("Status actualizado");
    } catch { toast.error("Error actualizando"); }
  };

  const guardarNotas = async () => {
    if (!seleccionado) return;
    try {
      await adminAPI.actualizarLead(seleccionado.id, { internal_notes: notas });
      setLeads((ls) => ls.map((l) => l.id === seleccionado.id ? { ...l, internal_notes: notas } : l));
      setSeleccionado((s) => ({ ...s, internal_notes: notas }));
      toast.success("Notas guardadas");
    } catch { toast.error("Error guardando notas"); }
  };

  const eliminarLead = async (lead) => {
    const ok = window.confirm(
        `¿Eliminar el lead de "${lead.name}"?\n\nEsta acción no se puede deshacer. ` +
        `Si es un cliente real que ya no interesa, mejor márcalo como "Descartado".`
    );
    if (!ok) return;

    setEliminando(true);
    try {
      await adminAPI.eliminarLead(lead.id);
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      setPagination((p) => ({ ...p, total: Math.max(0, p.total - 1) }));
      if (seleccionado?.id === lead.id) {
        cerrarDetalle();
        setNotas("");
      }
      toast.success("Lead eliminado");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error eliminando lead");
    } finally {
      setEliminando(false);
    }
  };

  // ── Detalle en celular ──
  // Al abrirlo se agrega un paso al historial, para que el botón "atrás" del celular
  // (o deslizar en iPhone) cierre el detalle en lugar de salir de la página.
  const esMovil = useEsMovil();
  const enHistorial = useRef(false);

  const abrirDetalle = (lead) => {
    setSeleccionado(lead);
    setNotas(lead.internal_notes || "");
    if (esMovil && !enHistorial.current) {
      window.history.pushState({ ...window.history.state, leadDetalle: true }, "");
      enHistorial.current = true;
    }
  };

  const cerrarDetalle = useCallback(() => {
    if (enHistorial.current) {
      window.history.back(); // el evento "popstate" de abajo lo cierra
    } else {
      setSeleccionado(null);
    }
  }, []);

  useEffect(() => {
    const alRegresar = () => {
      if (enHistorial.current) {
        enHistorial.current = false;
        setSeleccionado(null);
      }
    };
    window.addEventListener("popstate", alRegresar);
    return () => window.removeEventListener("popstate", alRegresar);
  }, []);

  const abrirModal = () => {
    setFormNuevo(FORM_VACIO);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setFormNuevo(FORM_VACIO);
  };

  const handleCrearLead = async (e) => {
    e.preventDefault();
    if (!formNuevo.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    if (!formNuevo.phone.trim() && !formNuevo.email.trim()) {
      toast.error("Escribe al menos un teléfono o un correo");
      return;
    }
    if (formNuevo.phone.trim() && !telefonoValido(formNuevo.phone)) {
      toast.error("El teléfono debe tener 10 dígitos");
      return;
    }
    if (formNuevo.email.trim() && !emailValido(formNuevo.email)) {
      toast.error("El correo no es válido");
      return;
    }
    setGuardando(true);
    try {
      const res = await adminAPI.crearLead(formNuevo);
      const nuevoLead = res.data.data;
      // Agregar al inicio de la lista
      setLeads((ls) => [nuevoLead, ...ls]);
      setPagination((p) => ({ ...p, total: p.total + 1 }));
      cerrarModal();
      toast.success("Lead creado correctamente");
      // Abrir el detalle del lead recién creado
      setSeleccionado(nuevoLead);
      setNotas(nuevoLead.internal_notes || "");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error creando lead");
    } finally {
      setGuardando(false);
    }
  };

  // Detalle del lead (se usa a un lado en computadora y en pantalla completa en celular)
  const renderDetalle = () => (
      <div className="lead-detalle card">
        <div className="lead-detalle-header">
          <div className="lead-big-avatar">{seleccionado.name.charAt(0).toUpperCase()}</div>
          <div>
            <h2 className="lead-detalle-name">{seleccionado.name}</h2>
            <p className="lead-detalle-fecha">Registrado el {formatFecha(seleccionado.created_at)}</p>
          </div>
        </div>

        <div className="lead-detalle-body">
          {seleccionado.phone && (
              <div className="lead-dato">
                <span className="lead-dato-label">📞 Teléfono / WhatsApp</span>
                <a href={`tel:${seleccionado.phone}`} className="lead-dato-val">{seleccionado.phone}</a>
              </div>
          )}
          {seleccionado.email && (
              <div className="lead-dato">
                <span className="lead-dato-label">✉️ Email</span>
                <a href={`mailto:${seleccionado.email}`} className="lead-dato-val">{seleccionado.email}</a>
              </div>
          )}
          {seleccionado.subject && (
              <div className="lead-dato">
                <span className="lead-dato-label">📋 Asunto</span>
                <p className="lead-dato-val">{seleccionado.subject}</p>
              </div>
          )}
          {seleccionado.property_title && (
              <div className="lead-dato">
                <span className="lead-dato-label">🏠 Propiedad de interés</span>
                <a href={`/propiedades/${seleccionado.property_slug}`} target="_blank" rel="noreferrer"
                   className="lead-dato-val">
                  {seleccionado.property_title}
                </a>
              </div>
          )}
          {seleccionado.payment_methods?.length > 0 && (
              <div className="lead-dato">
                <span className="lead-dato-label">💳 Forma de pago</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: ".35rem", marginTop: ".2rem" }}>
                  {seleccionado.payment_methods.map((m) => (
                      <span key={m} style={{
                        background: "var(--color-bg-secondary)", color: "var(--color-primary)",
                        padding: ".2rem .65rem", borderRadius: "999px", fontSize: ".8rem", fontWeight: 600,
                      }}>
                        {m === "OTRO" && seleccionado.payment_other
                            ? `Otro: ${seleccionado.payment_other}`
                            : etiquetaFormaPago(m)}
                      </span>
                  ))}
                </div>
              </div>
          )}
          {seleccionado.message && (
              <div className="lead-dato">
                <span className="lead-dato-label">💬 Mensaje</span>
                <p className="lead-dato-val lead-mensaje">{seleccionado.message}</p>
              </div>
          )}

          <div className="lead-dato">
            <span className="lead-dato-label">📊 Status</span>
            <select className="form-select" style={{ width: "auto" }}
                    value={seleccionado.status}
                    onChange={(e) => cambiarStatus(seleccionado.id, e.target.value)}>
              {Object.entries(CONTACT_STATUS).map(([v, s]) => (
                  <option key={v} value={v}>{s.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label private">🔒 Notas internas</label>
            <textarea className="form-textarea" rows={4} value={notas}
                      onChange={(e) => setNotas(e.target.value)}
                      placeholder="Escribe tus notas de seguimiento aquí..." />
            <button className="btn btn-primary btn-sm"
                    style={{ marginTop: ".5rem", alignSelf: "flex-start" }}
                    onClick={guardarNotas}>
              Guardar notas
            </button>
          </div>

          <div className="lead-acciones">
            {seleccionado.email && (
                <a href={`mailto:${seleccionado.email}`} className="btn btn-outline btn-sm">✉️ Email</a>
            )}
            {seleccionado.phone && (
                <>
                  <a href={`tel:${seleccionado.phone}`} className="btn btn-outline btn-sm">📞 Llamar</a>
                  <a href={`https://wa.me/52${normalizarTelefono(seleccionado.phone) || seleccionado.phone.replace(/\D/g, "")}`}
                     target="_blank" rel="noreferrer"
                     className="btn btn-sm" style={{ background: "#25D366", color: "#fff" }}>
                    💬 WhatsApp
                  </a>
                </>
            )}
          </div>

          {esSuperAdmin && (
              <div style={{
                marginTop: "1.5rem", paddingTop: "1rem",
                borderTop: "1px solid rgba(0,0,0,.08)",
                display: "flex", justifyContent: "flex-end",
              }}>
                <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ color: "#B91C1C" }}
                    disabled={eliminando}
                    onClick={() => eliminarLead(seleccionado)}
                >
                  {eliminando ? "Eliminando..." : "🗑️ Eliminar lead"}
                </button>
              </div>
          )}
        </div>
      </div>
  );

  return (
      <div className="leads-page fade-in">

        {/* ── Modal nuevo lead ── */}
        {modalAbierto && (
            <div className="modal-overlay" onClick={cerrarModal}>
              <div className="modal-box card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>➕ Nuevo lead</h3>
                  <button className="btn btn-ghost btn-sm" onClick={cerrarModal}>✕</button>
                </div>
                <form onSubmit={handleCrearLead} className="modal-form">
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label className="form-label">Nombre *</label>
                      <input
                          className="form-input"
                          value={formNuevo.name}
                          onChange={(e) => setFormNuevo({ ...formNuevo, name: e.target.value })}
                          placeholder="Nombre completo"
                          autoFocus
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Teléfono / WhatsApp</label>
                      <input
                          type="tel"
                          inputMode="tel"
                          className="form-input"
                          value={formNuevo.phone}
                          onChange={(e) => setFormNuevo({ ...formNuevo, phone: e.target.value })}
                          placeholder="10 dígitos"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input
                          type="email"
                          className="form-input"
                          value={formNuevo.email}
                          onChange={(e) => setFormNuevo({ ...formNuevo, email: e.target.value })}
                          placeholder="correo@ejemplo.com"
                      />
                    </div>
                    <p className="form-hint" style={{ gridColumn: "1 / -1", margin: 0 }}>
                      Escribe al menos el teléfono o el correo.
                    </p>
                    <div className="form-group">
                      <label className="form-label">Status inicial</label>
                      <select
                          className="form-select"
                          value={formNuevo.status}
                          onChange={(e) => setFormNuevo({ ...formNuevo, status: e.target.value })}
                      >
                        {Object.entries(CONTACT_STATUS).map(([v, s]) => (
                            <option key={v} value={v}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Mensaje / Descripción</label>
                    <textarea
                        className="form-textarea"
                        rows={3}
                        value={formNuevo.message}
                        onChange={(e) => setFormNuevo({ ...formNuevo, message: e.target.value })}
                        placeholder="¿Qué propiedad le interesa? ¿De dónde viene el contacto?"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label private">🔒 Notas internas</label>
                    <textarea
                        className="form-textarea"
                        rows={2}
                        value={formNuevo.internal_notes}
                        onChange={(e) => setFormNuevo({ ...formNuevo, internal_notes: e.target.value })}
                        placeholder="Observaciones privadas sobre este contacto..."
                    />
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn btn-ghost" onClick={cerrarModal}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-accent" disabled={guardando}>
                      {guardando ? "Guardando..." : "✅ Crear lead"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
        )}

        {/* ── Header ── */}
        <div className="page-header">
          <div>
            <h1 className="heading-serif page-title">Leads / Contactos</h1>
            <p style={{ color: "var(--color-text-muted)", fontSize: ".9rem" }}>
              {pagination.total} contactos en total
            </p>
          </div>
          <button className="btn btn-accent" onClick={abrirModal}>
            + Nuevo lead
          </button>
        </div>

        {/* ── Tabs de filtro ── */}
        <div className="leads-tabs">
          {["", "NUEVO", "EN_SEGUIMIENTO", "ATENDIDO", "DESCARTADO"].map((s) => {
            const info = CONTACT_STATUS[s] || { label: "Todos" };
            return (
                <button key={s}
                        className={`tab-btn ${filtroStatus === s ? "active" : ""}`}
                        onClick={() => { setFiltroStatus(s); cargar(s); }}>
                  {s === "" ? "Todos" : info.label}
                </button>
            );
          })}
        </div>

        <div className="leads-layout">
          {/* ── Lista ── */}
          <div className="leads-list-col card">
            {cargando ? (
                <div className="spinner" />
            ) : leads.length === 0 ? (
                <div style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                  <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>📭</div>
                  <p>No hay leads con este filtro</p>
                  <button className="btn btn-primary btn-sm" style={{ marginTop: "1rem" }} onClick={abrirModal}>
                    + Crear el primero
                  </button>
                </div>
            ) : (
                leads.map((lead) => {
                  const cs = CONTACT_STATUS[lead.status] || {};
                  const activo = seleccionado?.id === lead.id;
                  return (
                      <div key={lead.id}
                           className={`lead-item ${activo ? "active" : ""}`}
                           onClick={() => abrirDetalle(lead)}>
                        <div className="lead-item-avatar">{lead.name.charAt(0).toUpperCase()}</div>
                        <div className="lead-item-info">
                          <p className="lead-item-name">{lead.name}</p>
                          <p className="lead-item-prop">{lead.property_title || "Contacto general"}</p>
                          <p className="lead-item-date">{formatFecha(lead.created_at)}</p>
                        </div>
                        <span className="status-badge"
                              style={{ background: cs.bg, color: cs.color, fontSize: ".7rem", flexShrink: 0 }}>
                    {cs.label || lead.status}
                  </span>
                      </div>
                  );
                })
            )}
          </div>

          {/* ── Panel de detalle ── */}
          {!esMovil && (seleccionado ? renderDetalle() : (
              <div className="lead-empty card">
                <div style={{ fontSize: "3rem" }}>👆</div>
                <p>Selecciona un lead para ver el detalle</p>
                <button className="btn btn-outline btn-sm" style={{ marginTop: "1rem" }} onClick={abrirModal}>
                  + Crear nuevo lead
                </button>
              </div>
          ))}
        </div>

        {/* ── Celular: detalle en pantalla completa, encima de la lista ── */}
        {esMovil && seleccionado && (
            <ModalPortal onClose={cerrarDetalle}>
              <div className="lead-movil" role="dialog" aria-modal="true"
                   aria-label={`Detalle de ${seleccionado.name}`}>
                <div className="lead-movil-barra">
                  <button type="button" className="lead-movil-volver" onClick={cerrarDetalle}>
                    ← Volver
                  </button>
                  <span className="lead-movil-nombre">{seleccionado.name}</span>
                </div>
                <div className="lead-movil-cuerpo">
                  {renderDetalle()}
                </div>
              </div>
            </ModalPortal>
        )}
      </div>
  );
}